import type { ProductAvailabilities } from '@shared/gql/document-nodes'
import type { FC, ReactNode } from 'react'

import { useQuery } from '@apollo/client'
import { getConfig } from '@shared/config'
import { AlcoholSellingStatus, GetClientCartItemDocument } from '@shared/gql/document-nodes'
import { Button } from '@sok/design-system'
import { MAX_CARTITEM_COUNT } from 'domain/product'
import { createContext, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { pendingProductVar } from 'services/ClientCart/cache'
import { useClientCartItemCount } from 'services/ClientCart/hooks/use-client-cart-item-count'
import { useSetCartItemCount } from 'services/ClientCart/mutations/set-cart-item-count'
import { openDeliveryModal, openTimeAlcoholConflictModal } from 'services/Delivery/cache'
import { useSelectedDeliverySlot } from 'services/DeliverySlot/hooks'
import { useProductUserEvent } from 'services/ProductUserEvent/product-user-event'
import { useStoreId } from 'utils/hooks/store/use-store-id'
import { useTrackingContext } from 'utils/tracking/components/TrackingContextProvider'
import { GA4_addToCart, GA4_removeFromCart } from 'utils/tracking/track-add-to-cart'

import { QuantityModifier } from './Product/QuantityModifier'

interface CartItemContext {
  count: number
  onChange: (x: number) => void
  basicQuantityUnit: string
  quantityMultiplier: number
  isAgeLimitedByAlcohol: boolean
}

const { domain } = getConfig()

export const ctx = createContext<CartItemContext | null>(null)

export const CartItemContext: FC<{
  availabilities?: ProductAvailabilities[] | null
  basicQuantityUnit: string
  children?: ReactNode
  id: string
  isAgeLimitedByAlcohol: boolean
  productName: string
  quantityMultiplier: number
}> = (props) => {
  const setItemCount = useSetCartItemCount()
  const { selectedStoreId, storeId } = useStoreId()
  const cartItemCount = useClientCartItemCount(props.id)
  const { data } = useQuery(GetClientCartItemDocument, {
    variables: {
      id: props.id,
    },
  })
  const { sendProductUserAddToCartEvent } = useProductUserEvent()
  const trackingContext = useTrackingContext()

  const selectedSlot = useSelectedDeliverySlot()

  const alcoholSellingNotAllowedForSlot =
    selectedSlot.type === 'SUCCESS' &&
    selectedSlot.data.alcoholSellingAllowed === AlcoholSellingStatus.NotAllowed

  const { isSIdEnabled } = domain

  const onChange = useCallback(
    async (value: number) => {
      if (isSIdEnabled && !selectedStoreId) {
        openDeliveryModal()
        // Product without selected store is added to a pending queue until store is selected
        pendingProductVar({ productId: props.id, storeId, amount: value })
        return
      }

      setItemCount(props.id, value)

      const isAddingToCart = value > cartItemCount || cartItemCount === 0

      if (isAddingToCart) {
        await sendProductUserAddToCartEvent(props.id)
        GA4_addToCart({
          ...trackingContext,
          products: [
            {
              ...props,
              ...(data?.cartItem || {}),
              ean: props.id,
              name: props.productName,
              quantity: Math.abs(cartItemCount - value),
            },
          ],
        })
      } else {
        GA4_removeFromCart({
          ...trackingContext,
          products: [
            {
              ...props,
              ...(data?.cartItem || {}),
              ean: props.id,
              name: props.productName,
              quantity: Math.abs(cartItemCount - value),
            },
          ],
        })
      }

      if (
        // Only open delivery modal if adding items, not when removing them
        isAddingToCart &&
        props.isAgeLimitedByAlcohol &&
        alcoholSellingNotAllowedForSlot
      ) {
        openTimeAlcoholConflictModal()
      }
    },
    [
      alcoholSellingNotAllowedForSlot,
      cartItemCount,
      data?.cartItem,
      props,
      selectedStoreId,
      sendProductUserAddToCartEvent,
      setItemCount,
      storeId,
      trackingContext,
      isSIdEnabled,
    ],
  )

  return (
    <ctx.Provider
      value={{
        count: data?.cartItem?.itemCount ?? 0,
        onChange,
        basicQuantityUnit: props.basicQuantityUnit,
        quantityMultiplier: props.quantityMultiplier,
        isAgeLimitedByAlcohol: props.isAgeLimitedByAlcohol,
      }}
    >
      {props.children}
    </ctx.Provider>
  )
}

const useCartItemContext = () => {
  const c = useContext(ctx)
  if (c) {
    return c
  }
  throw new Error('useCartItemContext must be within <CartItemContext>')
}

export const CtxAddToCart: FC = () => {
  const { count, onChange, basicQuantityUnit, quantityMultiplier } = useCartItemContext()

  return (
    <QuantityModifier
      quantityUnit={basicQuantityUnit ?? ''}
      itemAmountMultiplier={quantityMultiplier || 1}
      max={MAX_CARTITEM_COUNT}
      min={0}
      onChange={onChange}
      value={count}
    />
  )
}

export const CtxAddToCartNoStoreSelected = () => {
  const { t } = useTranslation()
  const { count, onChange } = useCartItemContext()
  return (
    <Button
      data-test-id="add-to-cart-select-store"
      text={t`Choose store`}
      onClick={() => onChange(count)}
    />
  )
}
